/**
 * Ryan O'Dowd
 * 2022-05-06
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  GOAL_ROUTE,
  NEW_GOAL_ROUTE,
  setActiveGoalId,
} from '@utils/store/actions';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import CPassage from '@classes/Passage';
import Icon from '@react-native-vector-icons/material-design-icons';
import ListItem from '@components/ListItem';
import ProgressIndicator from '@components/ProgressIndicator';
import React from 'react';
import Text from '@components/common/Text';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const GoalProgress = (props: GoalProgressProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const passages = [];
  props.modules.forEach((m) => {
    m.assignments?.map((a) => a.assignment_object.passage).filter((x) => x).forEach((p) => {
      passages.push(p);
    });
  });
  const isMemoryGoal = props.goal_type === 'memory';
  const passagesVerses = hooks.useGetVersesForPassages(!isMemoryGoal ? [] : passages); // @NOTE: if reading goal, we don't need passages; this is a HUGE perf issue fetching all passages for huge goals (e.g. Bible in a year plan)

  const numWordsInGoal = !isMemoryGoal ? 0 : passages.reduce((curr, passage) => {
    return curr + passagesVerses[passage.id].map((pv) => pv.verseText).join(' ').split(' ').filter((x) => x).length;
  }, 0);
  const numVersesInGoal = passages.reduce((curr, passage) => {
    return curr + (passage.end_verse_number - passage.start_verse_number + 1);
  }, 0);

  const [completedWrinkles, inProgressWrinkles, numCompletedWords, numInProgressWords]: [any, any, any, any] = hooks.useTypedSelector((state) => { // @TODO: this is too complex // @TODO: use CPassage.getWrinkles() instead? // @TODO: get this from the back end instead since we're just getting the counts? // @TODO: this is tricky because if there's a passage that has multiple verses, we need to get the wrinkle for each of those verses, not just add verse nums if the whole passage/assignment is completed
    if (!isMemoryGoal) {
      return [0, 0, 0, 0];
    }
    const _completedWrinkles = [];
    const _inProgressWrinkles = [];
    let _numCompletedWords = 0;
    let _numInProgressWords = 0;
    passages.forEach((p) => {
      const book = state.books.find((b) => b.id === p.book_id);
      for (let verseNum = p.start_verse_number; verseNum <= p.end_verse_number; verseNum++) {
        const verseApiId = CPassage.getVerseApiId(book.api_id, p.chapter_number, verseNum);
        const wrinkle = state.wrinkles[verseApiId]?.find((wrinkleVariant) => wrinkleVariant.translation_id === p.translation_id);
        if (wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
          _completedWrinkles.push(wrinkle);
          _numCompletedWords += passagesVerses[p.id].filter((pv) => pv.verseId === wrinkle.verse_id).map((pv) => pv.verseText).join(' ').split(' ').filter((x) => x).length;
        } else if (wrinkle?.level_number > 0) {
          _inProgressWrinkles.push(wrinkle);
          _numInProgressWords += passagesVerses[p.id].filter((pv) => pv.verseId === wrinkle.verse_id).map((pv) => pv.verseText).join(' ').split(' ').filter((x) => x).length;
        }
      }
    });

    return [_completedWrinkles, _inProgressWrinkles, _numCompletedWords, _numInProgressWords];
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const progress: any = props.hideRings ? {} : utilities.getGoalProgress(props.id);
  const isComplete = props.isComplete || progress.numCompletedModules === props.modules.length;

  const editIcon = props.hideEdit ? null : ( // @TODO: classroom goals shouldn't be editable client side (yet?). and neither should previews
    <TouchableOpacity
      style={styles.editIconWrapper}
      onPress={(e) => {
        e.stopPropagation();
        navigation.navigate(NEW_GOAL_ROUTE, {
          goalId: props.id,
        });
      }}
    >
      <Icon style={styles.editIcon} name='pencil' />
    </TouchableOpacity>
  );

  const ringScaleFactor = styles.windowWidth > 700 ? styles.globalScale : (1 + (0.3 * (styles.globalScale - 1))); // @TODO: too hardcoded

  return (
    <ListItem
      onPress={() => {
        dispatch(setActiveGoalId(props.id));
        navigation.navigate(GOAL_ROUTE);
      }}
      disabled={props.hideTitleAndCreationDate}// @TODO: don't hijack the hideTitleAndCreationDate prop to see if this is clickable
      variant={isComplete ? 'accent' : 'primary'}
    >
      <View style={styles.columnWrapper}>
        {!props.hideTitleAndCreationDate &&
          <React.Fragment>
            <View style={styles.topRow}>
              <Text style={styles.title}>{props.title}</Text>
            </View>
            <Text style={styles.date}>{i18n('createdOn')} {dateFnsFormat(new Date(props.created_at), 'MMMM d, yyyy', {locale: i18n.dateFnsLocale})}</Text>
            {/* @TODO: target completion date? */}
          </React.Fragment>
        }
        {!props.hideRings &&
          <View style={styles.metadataWrapper}>
            <View style={styles.metadataRow}>
              <View style={styles.twoRingsWrapper}>
                <View style={styles.progressWrapper}>
                  {isMemoryGoal
                    ? (
                      <ProgressIndicator
                        category={i18n('passages')}
                        completedNum={progress.numCompletedAssignments}
                        inProgressNum={progress.numInProgressAssignments}
                        totalNum={passages.length}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    ) : (
                      <ProgressIndicator
                        category={i18n('modules')}
                        completedNum={progress.numCompletedModules}
                        inProgressNum={progress.numInProgressModules}
                        totalNum={props.modules.length}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    )
                  }
                </View>
                <View style={styles.progressWrapper}>
                  {isMemoryGoal
                    ? (
                      <ProgressIndicator
                        category={i18n('verses')}
                        completedNum={completedWrinkles.length}
                        inProgressNum={inProgressWrinkles.length}
                        totalNum={numVersesInGoal}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    ) : (
                      <ProgressIndicator
                        category={i18n('assignments')}
                        completedNum={progress.numCompletedAssignments}
                        inProgressNum={progress.numInProgressAssignments}
                        totalNum={props.modules.reduce((acc, m) => acc + m.assignments.length, 0)}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    )
                  }
                </View>
              </View>
              <View style={styles.twoRingsWrapper}>
                <View style={styles.progressWrapper}>
                  {isMemoryGoal
                    ? (
                      <ProgressIndicator
                        category={i18n('words')}
                        completedNum={numCompletedWords}
                        inProgressNum={numInProgressWords}
                        totalNum={numWordsInGoal}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    ) : (
                      <ProgressIndicator
                        category={i18n('verses')}
                        completedNum={progress.numVersesInCompletedAssignments}
                        inProgressNum={progress.numVersesInInProgressAssignments}
                        totalNum={numVersesInGoal}
                        showInnerText={true}
                        scaleFactor={ringScaleFactor}
                      />
                    )
                  }
                </View>
                <View style={styles.progressWrapper}>
                  <ProgressIndicator
                    category={i18n('peanuts')}
                    completedNum={progress.numPeanutsEarned}
                    inProgressNum={progress.numPeanutsEarned}
                    totalNum={progress.numPeanutsEarned}
                    color={isComplete ? styles.colors.complete : styles.colors.primaryDark}
                    backgroundColor={isComplete ? (styles.colors.completeBg) : styles.colors.primary}
                    showInnerText={true}
                    scaleFactor={ringScaleFactor}
                  />
                </View>
              </View>
            </View>
          </View>
        }
      </View>
      {editIcon}
    </ListItem>
  );
};

class GoalProgressProps {
  created_at: string; // @TODO: date object?
  id: number;
  title: string;
  goal_type: 'group' | 'memory' | 'reading';
  modules: any[];

  hideTitleAndCreationDate?: boolean;
  hideEdit?: boolean;
  hideRings?: boolean;
  isComplete?: boolean;
};

export default GoalProgress;
